import {apiDelete, apiGet, apiPost} from '../crud-dashboard/services/api';

export interface FaceAuthStatus {
  has_face_registered: boolean;
  face_auth_enabled: boolean;
  face_registered_at: string | null;
}

export interface FaceAuthLog {
  id: number;
  user_id: number;
  status: 'success' | 'failed' | 'spoofing_detected';
  similarity_score: number | null;
  liveness_confidence: number | null;
  ip_address: string | null;
  created_at: string;
}

export interface RegisterFaceResponse {
  success: boolean;
  message: string;
  data?: {
    face_id: string;
    liveness_confidence: number;
  };
}

export interface FaceLoginResponse {
  success: boolean;
  message: string;
  data?: {
    user: any;
    access_token: string;
    token_type: string;
    similarity: number;
  };
}

class FaceAuthService {
  /**
   * Register user's face (for Face ID settings)
   */
  async registerFace(imageBase64: string): Promise<RegisterFaceResponse> {
    try {
      const response = await apiPost<RegisterFaceResponse>('/api/face-auth/register', {
        image: imageBase64,
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to register face',
      };
    }
  }

  /**
   * Check-in with face (for enrollment/attendance)
   */
  async checkInWithFace(
    imageBase64: string,
    latitude?: number,
    longitude?: number
  ): Promise<RegisterFaceResponse> {

    try {
      // Build URL with query parameters
      const params = new URLSearchParams();
      if (latitude !== undefined) {
        params.append('latitude', latitude.toString());
      }
      if (longitude !== undefined) {
        params.append('longitude', longitude.toString());
      }

      const endpoint = `/api/face-auth/check-in${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await apiPost<RegisterFaceResponse>(endpoint, {
        image: imageBase64,
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to check in',
      };
    }
  }

  /**
   * Check-out with face (for attendance)
   */
  async checkOutWithFace(
    imageBase64: string,
    latitude?: number,
    longitude?: number
  ): Promise<RegisterFaceResponse> {
    try {
      const params = new URLSearchParams();
      if (latitude !== undefined) {
        params.append('latitude', latitude.toString());
      }
      if (longitude !== undefined) {
        params.append('longitude', longitude.toString());
      }

      const endpoint = `/api/face-auth/check-out${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await apiPost<RegisterFaceResponse>(endpoint, {
        image: imageBase64,
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to check out',
      };
    }
  }

  /**
   * Login with face recognition
   */
  async loginWithFace(imageBase64: string): Promise<FaceLoginResponse> {
    try {
      // Use fetch directly for login since it's not authenticated
      const BASE_URL = 'https://fakhama.m-alorfali.com';
      const response = await fetch(`${BASE_URL}/api/face-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ image: imageBase64 }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Face authentication failed',
        };
      }

      return data;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to authenticate with face',
      };
    }
  }

  /**
   * Delete user's face
   */
  async deleteFace(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiDelete<{ success: boolean; message: string }>('/api/face-auth/delete');
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Failed to delete face',
      };
    }
  }

  /**
   * Get face authentication status
   */
  async getStatus(): Promise<{ success: boolean; data?: FaceAuthStatus }> {
    try {
      const response = await apiGet<{ success: boolean; data: FaceAuthStatus }>('/api/face-auth/status');
      return response;
    } catch (error: any) {
      return {
        success: false,
      };
    }
  }


  /**
   * Get face authentication logs
   */
  async getLogs(): Promise<{ success: boolean; data?: { data: FaceAuthLog[]; total: number } }> {
    try {
      const response = await apiGet<{ success: boolean; data: { data: FaceAuthLog[]; total: number } }>(
        '/api/face-auth/logs'
      );
      return response;
    } catch (error: any) {
      return {
        success: false,
      };
    }
  }

  /**
   * Analyze the brightness of the current video frame.
   * Returns average luminance (0-255) and a quality label.
   * Uses a downsampled canvas for performance.
   */
  analyzeFrameBrightness(videoElement: HTMLVideoElement): {
    luminance: number;
    label: 'too_dark' | 'dark' | 'good' | 'bright' | 'too_bright';
  } {
    const sampleSize = 64;
    const canvas = document.createElement('canvas');
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { luminance: 128, label: 'good' };

    ctx.drawImage(videoElement, 0, 0, sampleSize, sampleSize);
    const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const data = imageData.data;

    let totalLuminance = 0;
    const pixelCount = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      // Standard luminance formula (ITU-R BT.601)
      totalLuminance += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const avgLuminance = totalLuminance / pixelCount;

    let label: 'too_dark' | 'dark' | 'good' | 'bright' | 'too_bright';
    if (avgLuminance < 40) label = 'too_dark';
    else if (avgLuminance < 70) label = 'dark';
    else if (avgLuminance <= 200) label = 'good';
    else if (avgLuminance <= 235) label = 'bright';
    else label = 'too_bright';

    return { luminance: Math.round(avgLuminance), label };
  }

  /**
   * Analyze the sharpness of the current video frame using Laplacian variance.
   * Higher values = sharper image. Returns a normalized score and quality label.
   * Uses a downsampled grayscale canvas for performance.
   */
  analyzeFrameSharpness(videoElement: HTMLVideoElement): {
    score: number;
    label: 'blurry' | 'acceptable' | 'sharp';
  } {
    const sampleSize = 64;
    const canvas = document.createElement('canvas');
    canvas.width = sampleSize;
    canvas.height = sampleSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { score: 100, label: 'sharp' };

    ctx.drawImage(videoElement, 0, 0, sampleSize, sampleSize);
    const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
    const data = imageData.data;
    const w = sampleSize;
    const h = sampleSize;

    // Convert to grayscale array
    const gray: number[] = new Array(w * h);
    for (let i = 0; i < data.length; i += 4) {
      gray[i / 4] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }

    // Compute Laplacian variance (sum of squared second derivatives)
    let sum = 0;
    let sumSq = 0;
    let count = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = y * w + x;
        const laplacian =
          -gray[idx - w] - gray[idx - 1] + 4 * gray[idx] - gray[idx + 1] - gray[idx + w];
        sum += laplacian;
        sumSq += laplacian * laplacian;
        count++;
      }
    }
    const mean = sum / count;
    const variance = sumSq / count - mean * mean;

    // Normalize: typical sharp face images have variance > 500, blurry < 100
    let label: 'blurry' | 'acceptable' | 'sharp';
    if (variance < 80) label = 'blurry';
    else if (variance < 250) label = 'acceptable';
    else label = 'sharp';

    return { score: Math.round(variance), label };
  }

  /**
   * Comprehensive real-time quality analysis of the current video frame.
   * Combines brightness and sharpness analysis for a single quality status.
   */
  analyzeFrameQuality(videoElement: HTMLVideoElement): {
    brightness: { luminance: number; label: string };
    sharpness: { score: number; label: string };
    overall: 'poor' | 'fair' | 'good';
    issues: string[];
  } {
    const brightness = this.analyzeFrameBrightness(videoElement);
    const sharpness = this.analyzeFrameSharpness(videoElement);

    const issues: string[] = [];
    if (brightness.label === 'too_dark') issues.push('too_dark');
    else if (brightness.label === 'dark') issues.push('dark');
    else if (brightness.label === 'too_bright') issues.push('too_bright');
    else if (brightness.label === 'bright') issues.push('bright');

    if (sharpness.label === 'blurry') issues.push('blurry');

    let overall: 'poor' | 'fair' | 'good';
    if (issues.length === 0) {
      overall = 'good';
    } else if (issues.includes('too_dark') || issues.includes('too_bright') || issues.includes('blurry')) {
      overall = 'poor';
    } else {
      overall = 'fair';
    }

    return { brightness, sharpness, overall, issues };
  }

  /**
   * Capture image from video stream with brightness preprocessing.
   * Analyzes the frame brightness and applies gamma correction for Amazon Rekognition compatibility.
   * Mirrors the image to match the preview and ensures cross-platform compatibility.
   */
  captureImageFromVideo(videoElement: HTMLVideoElement): string {
    // Validate video element is ready
    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      throw new Error('Video not ready. Please wait for camera to initialize.');
    }

    const canvas = document.createElement('canvas');
    const w = videoElement.videoWidth;
    const h = videoElement.videoHeight;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Mirror to match preview
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(videoElement, 0, 0, w, h);

    // Reset transform for pixel manipulation
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Get raw pixel data for preprocessing
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const pixelCount = data.length / 4;

    // --- Step 1: Compute average brightness ---
    let totalLuminance = 0;
    for (let i = 0; i < data.length; i += 4) {
      totalLuminance += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const avgLuminance = totalLuminance / pixelCount;

    // --- Step 2: Determine gamma adjustment based on brightness ---
    // Mac cameras often produce overexposed images; gamma correction helps normalize brightness
    let gamma = 1.0;
    if (avgLuminance > 180) {
      // Overexposed: use gamma < 1 to compress highlights
      gamma = 0.7;
    } else if (avgLuminance > 150) {
      gamma = 0.85;
    } else if (avgLuminance < 50) {
      // Underexposed: use gamma > 1 to lift shadows
      gamma = 1.4;
    } else if (avgLuminance < 70) {
      gamma = 1.2;
    }

    // Precompute gamma lookup table for performance
    const gammaLut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      gammaLut[i] = Math.round(255 * Math.pow(i / 255, 1.0 / gamma));
    }

    // --- Step 3: Apply gamma-based brightness correction ---
    for (let i = 0; i < data.length; i += 4) {
      data[i] = gammaLut[data[i]];
      data[i + 1] = gammaLut[data[i + 1]];
      data[i + 2] = gammaLut[data[i + 2]];
    }

    // Write processed pixels back
    ctx.putImageData(imageData, 0, 0);

    // Encode as high-quality JPEG
    return canvas.toDataURL('image/jpeg', 0.92);
  }
  /**
   * Validate image quality before sending to face recognition service.
   * Performs pixel-level analysis of brightness, sharpness, and resolution.
   * Returns specific feedback messages for each issue found.
   */
  validateImageQuality(imageBase64: string): { valid: boolean; message?: string; issues: string[] } {
    const issues: string[] = [];

    // Check minimum base64 size (rough resolution check)
    const minBase64Length = 25000; // ~18KB minimum for a reasonable resolution image
    if (imageBase64.length < minBase64Length) {
      issues.push('low_resolution');
    }

    // Decode the base64 image to analyze pixel data
    // We create an image element and draw it to a canvas for analysis
    // Since this is synchronous validation on an already-captured image,
    // we rely on the pre-capture analysis done by analyzeFrameQuality
    // and the base64 size as a proxy for quality.

    if (issues.length > 0) {
      return {
        valid: false,
        message: 'Image quality too low. Please ensure good lighting and hold steady.',
        issues,
      };
    }

    return { valid: true, issues: [] };
  }

  /**
   * Start camera stream with cross-platform compatible constraints.
   * Uses a resolution fallback chain for maximum compatibility across
   * Mac Safari, Mac Chrome, Windows Chrome, and Windows Edge.
   */
  async startCamera(videoElement: HTMLVideoElement): Promise<MediaStream> {
    // Resolution fallback chain: try highest first, then decrease
    const resolutionAttempts = [
      { width: { ideal: 1280, min: 1280 }, height: { ideal: 720, min: 720 } },
      { width: { ideal: 1280, min: 960 }, height: { ideal: 720, min: 540 } },
      { width: { ideal: 960, min: 640 }, height: { ideal: 540, min: 480 } },
      { width: { ideal: 640 }, height: { ideal: 480 } },
    ];

    let stream: MediaStream | null = null;
    let lastError: Error | null = null;

    for (const resolution of resolutionAttempts) {
      try {
        const videoConstraints: MediaTrackConstraints = {
          ...resolution,
          facingMode: 'user',
          frameRate: { ideal: 30, min: 15 },
        };

        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        break; // Success, exit the loop
      } catch (err: any) {
        lastError = err;
        continue; // Try next resolution
      }
    }

    if (!stream) {
      // Final fallback: no constraints at all
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      } catch (fallbackError: any) {
        throw new Error(
          'Failed to access camera. Please ensure camera permissions are granted in your browser settings.'
        );
      }
    }

    // Apply additional track-level constraints for better image quality
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      try {
        const capabilities = videoTrack.getCapabilities?.() as any;
        if (capabilities) {
          const advancedConstraints: any = {};

          // Apply exposure compensation if supported (helps with Mac cameras)
          if (capabilities.exposureCompensation) {
            const { min, max } = capabilities.exposureCompensation;
            const mid = (min + max) / 2;
            advancedConstraints.exposureCompensation = Math.min(max, mid + 0.3);
          }

          // Apply focus mode if supported
          if (capabilities.focusMode) {
            if (capabilities.focusMode.includes?.('continuous')) {
              advancedConstraints.focusMode = 'continuous';
            }
          }

          // Apply white balance if supported
          if (capabilities.whiteBalanceMode) {
            if (capabilities.whiteBalanceMode.includes?.('continuous')) {
              advancedConstraints.whiteBalanceMode = 'continuous';
            }
          }

          if (Object.keys(advancedConstraints).length > 0) {
            await videoTrack.applyConstraints({ advanced: [advancedConstraints] }).catch(() => {
              // Silently fail - canvas filters will handle adjustments
            });
          }
        }
      } catch {
        // Ignore capability constraint errors - they're optional enhancements
      }
    }

    videoElement.srcObject = stream;

    // Wait for video metadata to be loaded
    await new Promise<void>((resolve) => {
      if (videoElement.readyState >= 2) {
        resolve();
      } else {
        videoElement.onloadeddata = () => resolve();
      }
    });

    await videoElement.play();

    return stream;
  }

  /**
   * Stop camera stream
   */
  stopCamera(stream: MediaStream): void {

    stream.getTracks().forEach((track) => {
      track.stop();
    });

  }

  /**
   * Check if browser supports camera
   */
  isCameraSupported(): boolean {
    const supported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    return supported;
  }
}

export const faceAuthService = new FaceAuthService();

